import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { calendarApi } from './api';
import { eventUrl, itemWhen, tasksUrl } from './calendarToday';
import { DEFAULT_POS, clampPos, clearPos, dragPos, loadPos, noticeView, savePos } from './calendarNotices';
import useCalendarNotices from './useCalendarNotices';
import NotesReader from './NotesReader';
import { ReminderRow, TodayItemRow, VIOLET } from './TodayRows';
import { Z_CALENDAR_CARD } from './layers';

// =============================================================================
// 5.50.0 the ONE calendar notice on the MAIN InsureDocs page and the HEALTHSHERPA page (Henry 2026-11-03), replacing the
// red Today panel there and the purple reminder pop-up (both duplicated each other). The Calendar page keeps its red
// panel (TodayPanel.jsx), which now also carries the reminders.
//   - today's OWN Personal events + tasks due today — the 5.34.0 rules unchanged (the server decides; no Office events,
//     admins only their own, a task without a due date never shows, gone after the day, a new item after ✕ brings it back)
//   - a reminder that comes due (same timing as before) jumps to the TOP, highlighted, with Dismiss and Open calendar;
//     it shows even when the list was minimized / closed (only its own Dismiss clears it); Dismiss holds everywhere
//   - – minimizes to a small tab (click to reopen), ✕ closes for the rest of today (no tab) — per user per day on the
//     server, as the red panel did
//   - DRAG the header anywhere; the spot is remembered on THIS computer (localStorage, like the calendar text size) as
//     a distance from the bottom-right corner, kept on screen when the window shrinks; ↘ "Reset position" puts it back
// Layer: Z_CALENDAR_CARD — above page content, below the HealthSherpa lock strip (never covers its Clear), the top-bar
// menus and every modal. The full-page notes reader is portalled to <body> so the card's layer can't trap it.
// =============================================================================

const BG = '#1f1530';
const W = 320;

function usePosition(ref, deps) {
  const [stored, setStored] = useState(() => loadPos());
  const [pos, setPos] = useState(() => stored || DEFAULT_POS);
  const fit = useCallback((p) => {
    const el = ref.current;
    const w = el ? el.offsetWidth : W, h = el ? el.offsetHeight : 120;
    // the visible area WITHOUT scrollbars: position: fixed offsets are measured inside them (innerWidth includes them)
    const de = document.documentElement;
    return clampPos(p, w, h, (de && de.clientWidth) || window.innerWidth, (de && de.clientHeight) || window.innerHeight);
  }, [ref]);
  // keep it on screen: on resize, and whenever the card's size changes (items come and go)
  useLayoutEffect(() => {
    const again = () => setPos((p) => { const q = fit(stored || p); return q.right === p.right && q.bottom === p.bottom ? p : q; });
    again();
    window.addEventListener('resize', again);
    return () => window.removeEventListener('resize', again);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fit, stored, ...deps]);
  const drag = (e) => {
    if (e.button !== 0 || e.target.closest('button,input,a')) return;
    e.preventDefault();
    const start = { x: e.clientX, y: e.clientY, ...pos };
    let last = pos;
    const move = (ev) => { last = fit(dragPos(start, ev.clientX - start.x, ev.clientY - start.y)); setPos(last); };
    const up = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      if (last.right !== start.right || last.bottom !== start.bottom) { savePos(last); setStored(last); }
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const reset = () => { clearPos(); setStored(null); setPos(fit(DEFAULT_POS)); };
  return { pos, drag, reset, moved: !!stored };
}

export default function TodayCard({ onOpenClient, onOpenCalendar }) {
  const { today: data, alerts, rest, busy, err, setState, complete, dismissReminder, reload } = useCalendarNotices();
  const [reader, setReader] = useState(null);
  const ref = useRef(null);
  const view = noticeView(data, alerts);
  const { pos, drag, reset, moved } = usePosition(ref, [view.mode, alerts.length, rest.length, view.showToday]);
  useEffect(() => { if (view.mode === 'none') setReader(null); }, [view.mode]);

  const openItem = (i) => {
    if (i.kind === 'task') { window.open(tasksUrl(), 'insuredocs-calendar'); return; }
    window.open(eventUrl(data.day, i.object_id), 'insuredocs-calendar');
  };
  const openReminder = (r) => {
    const a = alerts.find((x) => x.reminder.key === r.key);
    if (a && a.item) { openItem(a.item); return; }
    if (onOpenCalendar) onOpenCalendar(); else window.open('/?calendar=1', 'insuredocs-calendar');
  };

  if (view.mode === 'none') return null;
  const hasToday = !!(data && data.enabled && data.count > 0);
  const place = { position: 'fixed', right: pos.right, bottom: pos.bottom, zIndex: Z_CALENDAR_CARD };

  if (view.mode === 'tab') {
    return (
      <button type="button" ref={ref} data-testid="today-tab" onClick={() => setState('open')} title={`Today — ${data.count} item${data.count === 1 ? '' : 's'}. Click to open.`}
        style={{ ...place, background: VIOLET, color: '#fff', border: 'none', borderRadius: 18, padding: '7px 12px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 14px rgba(0,0,0,0.4)', fontWeight: 700, fontSize: 13 }}>
        <span>📅 Today</span>
        <span data-testid="today-tab-count" style={{ background: '#fff', color: VIOLET, borderRadius: 10, minWidth: 20, padding: '1px 6px', fontSize: 12, textAlign: 'center' }}>{data.count}</span>
      </button>
    );
  }

  const hbtn = { background: 'rgba(0,0,0,0.25)', color: '#fff', border: 'none', borderRadius: 5, width: 24, height: 22, cursor: 'pointer', fontWeight: 700, flex: 'none' };
  const readerEl = reader && data && createPortal(
    <NotesReader title={reader.title} notes={reader.notes} canEdit={!!reader.can_edit && !!reader.etag}
      when={`${itemWhen(reader, data.day)} · Today, ${data.label} (Central)`}
      onSave={async (text) => { await calendarApi.update(reader.object_id, reader.etag, { notes: text }); reload(); return true; }}
      onClose={() => setReader(null)} />, document.body);

  return (
    <div ref={ref} data-testid="today-card" role="complementary" aria-label="Today"
      style={{ ...place, width: W, maxWidth: 'calc(100vw - 16px)', maxHeight: '70vh', display: 'flex', flexDirection: 'column', background: BG,
        border: `2px solid ${VIOLET}`, borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,0.45)', color: '#fff', overflow: 'hidden', boxSizing: 'border-box' }}>
      <div onPointerDown={drag} data-testid="today-card-header" title="Drag to move"
        style={{ background: VIOLET, padding: '7px 10px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'move', userSelect: 'none', touchAction: 'none' }}>
        <div style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} data-testid="today-title">
          📅 Today{data ? ` — ${data.label}` : ''}</div>
        {hasToday && <span data-testid="today-count" style={{ background: '#fff', color: VIOLET, borderRadius: 10, padding: '1px 7px', fontSize: 12, fontWeight: 700 }}>{data.count}</span>}
        {moved && <button type="button" onClick={reset} data-testid="today-card-reset" title="Reset position (back to the bottom-right corner)" aria-label="Reset position" style={hbtn}>↘</button>}
        {view.canHide && (
          <>
            <button type="button" onClick={() => setState('minimized')} data-testid="today-minimize" title="Minimize to a tab" style={hbtn}>–</button>
            <button type="button" onClick={() => setState('dismissed')} data-testid="today-dismiss" title="Close for the rest of today (it comes back if something new is added for today)" style={hbtn}>✕</button>
          </>
        )}
      </div>
      {err && <div style={{ background: '#7f1d1d', color: '#fecaca', fontSize: 12, padding: '5px 10px' }}>{err}</div>}
      <div style={{ overflowY: 'auto' }}>
        {alerts.map(({ reminder }) => (
          <ReminderRow key={reminder.key} r={reminder} onDismiss={dismissReminder} onOpenCalendar={openReminder} />
        ))}
        {view.showReopen && (
          <button type="button" onClick={() => setState('open')} data-testid="today-reopen"
            style={{ display: 'block', width: '100%', padding: '7px 10px', background: 'none', border: 'none', color: '#ddd6fe', fontSize: 12, cursor: 'pointer', textAlign: 'left' }}>
            Show today's {data.count} item{data.count === 1 ? '' : 's'} ▾</button>
        )}
        {view.showToday && rest.map((i) => (
          <TodayItemRow key={i.id} i={i} day={data.day} accent={VIOLET} sub="#e5d5f5" busy={busy}
            onComplete={complete} onOpen={openItem} onExpand={setReader} onOpenClient={onOpenClient} />
        ))}
      </div>
      {readerEl}
    </div>
  );
}
